import React, { useState, useEffect, useCallback, useRef } from 'react';
import Dashboard from './components/Dashboard';
import { AuthShell } from './components/Auth';
import { ForgotPassword, ResetPassword } from './components/PasswordReset';
import { AuthTokens, getUserInfo, logout, refreshTokens } from './adapters/authentication';
import { axiosInstance, AUTH_SESSION_EXPIRED_EVENT, AUTH_SESSION_REFRESHED_EVENT } from './adapters/instance';
import { readStoredAuthTokens, writeStoredAuthTokens, clearStoredAuthTokens } from './utils/authStorage';

type AuthState = AuthTokens & { user?: any };

const path = window.location.pathname;

function App() {
  const [auth, setAuth] = useState<AuthState | null>(() => {
    const stored = readStoredAuthTokens();
    if (!stored?.access && !stored?.refresh) return null;
    return { access: stored.access ?? undefined, refresh: stored.refresh ?? undefined };
  });
  const [isSessionChecked, setIsSessionChecked] = useState(false);
  const refreshTimerRef = useRef<number | null>(null);

  // Sync tokens to axios headers + localStorage
  useEffect(() => {
    if (auth?.access) {
      axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${auth.access}`;
    } else {
      delete axiosInstance.defaults.headers.common['Authorization'];
    }
    if (auth?.access || auth?.refresh) {
      writeStoredAuthTokens({ access: auth?.access ?? null, refresh: auth?.refresh ?? null });
    } else {
      clearStoredAuthTokens();
    }
  }, [auth?.access, auth?.refresh]);

  // Session expired / refreshed events from axios interceptor
  useEffect(() => {
    const handleExpired = () => setAuth(null);
    const handleRefreshed = (e: Event) => {
      const { access, refresh } = (e as CustomEvent<{ access?: string; refresh?: string }>).detail;
      setAuth(prev => prev ? { ...prev, access: access ?? prev.access, refresh: refresh ?? prev.refresh } : prev);
    };
    window.addEventListener(AUTH_SESSION_EXPIRED_EVENT, handleExpired);
    window.addEventListener(AUTH_SESSION_REFRESHED_EVENT, handleRefreshed);
    return () => {
      window.removeEventListener(AUTH_SESSION_EXPIRED_EVENT, handleExpired);
      window.removeEventListener(AUTH_SESSION_REFRESHED_EVENT, handleRefreshed);
    };
  }, []);

  // Validate stored session on mount (skip for auth pages)
  useEffect(() => {
    if (path === '/login' || path === '/signup' || path === '/forgot-password' || path.startsWith('/reset-password')) {
      setIsSessionChecked(true);
      return;
    }
    if (!auth?.access && !auth?.refresh) {
      setIsSessionChecked(true);
      return;
    }
    let mounted = true;
    getUserInfo().then(({ data, error }) => {
      if (!mounted) return;
      if (!error && data) {
        setAuth(prev => ({ ...prev, user: data }));
      } else {
        setAuth(null);
      }
    }).catch(() => {
      if (mounted) setAuth(null);
    }).finally(() => {
      if (mounted) setIsSessionChecked(true);
    });
    return () => { mounted = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Proactive token refresh (refresh 1 minute before expiry)
  const decodeExpiry = useCallback((token?: string | null) => {
    if (!token) return null;
    const parts = token.split('.');
    if (parts.length < 2) return null;
    try {
      const seg = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const padded = seg.padEnd(seg.length + ((4 - (seg.length % 4)) % 4), '=');
      const payload = JSON.parse(atob(padded));
      return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
    } catch { return null; }
  }, []);

  useEffect(() => {
    if (refreshTimerRef.current) { window.clearTimeout(refreshTimerRef.current); refreshTimerRef.current = null; }
    if (!auth?.access) return;
    const expiry = decodeExpiry(auth.access);
    if (!expiry) return;
    const token = auth.refresh;
    const doRefresh = async () => {
      try {
        const refreshed = await refreshTokens(token ?? null);
        setAuth(prev => prev ? { ...prev, access: refreshed.access ?? prev.access, refresh: refreshed.refresh ?? prev.refresh } : prev);
      } catch (e: any) {
        if (e?.response?.status === 401 || e?.response?.status === 403) setAuth(null);
      }
    };
    const timeout = Math.max(0, expiry - Date.now() - 60_000);
    if (timeout <= 0) { void doRefresh(); return; }
    const id = window.setTimeout(() => { void doRefresh(); refreshTimerRef.current = null; }, timeout);
    refreshTimerRef.current = id;
    return () => { window.clearTimeout(id); };
  }, [auth?.access, auth?.refresh, decodeExpiry]);

  const handleLogin = useCallback((tokens: AuthState) => {
    setAuth(tokens);
    window.location.replace('/');
  }, []);

  const handleLogout = useCallback(async () => {
    await logout();
    setAuth(null);
    window.location.replace('/login');
  }, []);

  // Auth pages render immediately without session check
  if (path === '/login') return <AuthShell mode="login" onLogin={handleLogin} />;
  if (path === '/signup') return <AuthShell mode="signup" onLogin={handleLogin} />;
  if (path === '/forgot-password') return <ForgotPassword />;
  if (path.startsWith('/reset-password')) return <ResetPassword />;

  // Dashboard: wait for session validation
  if (!isSessionChecked) return null;

  if (!auth?.user) {
    window.location.replace('/login');
    return null;
  }

  return <Dashboard onLogout={handleLogout} user={auth.user} />;
}

export default App;
